import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Building2, Users, ClipboardList, Wrench, FileText, Calendar, MapPin, TrendingUp } from 'lucide-react';

const Dashboard = ({ selectedLocation = 'All Locations' }) => {
  // Filter data based on selected location
  const getLocationStats = () => {
    if (selectedLocation === 'All Locations') {
      return {
        totalOffices: 124,
        leasedOffices: 98,
        vacantOffices: 18,
        onNoticeOffices: 8,
        memberCompanies: 156,
        totalEmployees: 2340,
        openTasks: 23,
        inProgressTasks: 12,
        templates: 45
      };
    } else {
      return {
        totalOffices: 15,
        leasedOffices: 12,
        vacantOffices: 2,
        onNoticeOffices: 1,
        memberCompanies: 18,
        totalEmployees: 285,
        openTasks: 7,
        inProgressTasks: 3,
        templates: 12
      };
    }
  };

  const stats = getLocationStats();

  const dashboardCards = [
    {
      title: 'Total Offices',
      value: stats.totalOffices.toString(),
      subtitle: `${stats.leasedOffices} Leased, ${stats.vacantOffices} Vacant, ${stats.onNoticeOffices} On-Notice`,
      icon: Building2,
      color: 'bg-slate-600'
    },
    {
      title: 'Member Companies',
      value: stats.memberCompanies.toString(),
      subtitle: `${stats.totalEmployees} total employees`,
      icon: Users,
      color: 'bg-amber-600'
    },
    {
      title: 'Open Tasks',
      value: stats.openTasks.toString(),
      subtitle: `${stats.inProgressTasks} in progress, ${stats.openTasks - stats.inProgressTasks} pending`,
      icon: Wrench,
      color: 'bg-orange-600'
    },
    {
      title: 'Templates',
      value: stats.templates.toString(),
      subtitle: 'Email & notification templates',
      icon: FileText,
      color: 'bg-indigo-600'
    }
  ];

  const recentActivity = [
    { 
      type: 'Office', 
      message: selectedLocation !== 'All Locations' 
        ? `${selectedLocation}: Office 205 status changed to Vacant` 
        : 'Greenpoint: Office 205 status changed to Vacant', 
      time: '2 hours ago' 
    },
    { 
      type: 'Task', 
      message: selectedLocation !== 'All Locations'
        ? `${selectedLocation}: Repaint hallway - marked as completed`
        : '55 Broadway: Repaint hallway - marked as completed', 
      time: '4 hours ago' 
    },
    { 
      type: 'Member', 
      message: selectedLocation !== 'All Locations'
        ? `${selectedLocation}: New employee added to TechCorp Solutions`
        : 'East Austin: New employee added to TechCorp Solutions', 
      time: '6 hours ago' 
    },
    { 
      type: 'Template', 
      message: 'Holiday notification template updated', 
      time: '1 day ago' 
    }
  ];

  const upcomingTasks = [
    { 
      task: selectedLocation !== 'All Locations' 
        ? `Fix broken light in Suite 301`
        : `${selectedLocation === 'All Locations' ? 'Bushwick:' : ''} Fix broken light in Suite 301`, 
      priority: 'High', 
      dueDate: 'Today' 
    },
    { 
      task: selectedLocation !== 'All Locations'
        ? `Replace air filter in common area`
        : `60 Broad: Replace air filter in common area`, 
      priority: 'Medium', 
      dueDate: 'Tomorrow' 
    },
    { 
      task: selectedLocation !== 'All Locations'
        ? `Touch-up paint in reception`
        : `Center City: Touch-up paint in reception`, 
      priority: 'Low', 
      dueDate: 'This week' 
    }
  ];

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center space-x-3">
            <h1 className="text-3xl font-bold text-slate-800">Bond Collective Operations</h1>
            {selectedLocation !== 'All Locations' && (
              <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-300">
                <MapPin className="w-3 h-3 mr-1" />
                {selectedLocation}
              </Badge>
            )}
          </div>
          <p className="text-gray-600 mt-2">
            {selectedLocation === 'All Locations' 
              ? 'Manage all Bond Collective locations efficiently' 
              : `Managing operations for ${selectedLocation}`}
          </p>
        </div>
        <div className="flex items-center space-x-2 text-sm text-gray-500">
          <Calendar className="w-4 h-4" />
          <span>{new Date().toLocaleDateString('en-US', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })}</span>
        </div>
      </div>

      {/* Location Summary (only show when viewing all locations) */}
      {selectedLocation === 'All Locations' && (
        <Card className="bg-gradient-to-r from-slate-50 to-gray-50 border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-800 mb-2">Multi-Location Overview</h3>
                <p className="text-gray-600">Managing 8 locations across NYC, Philadelphia, and Austin</p>
              </div>
              <TrendingUp className="w-8 h-8 text-slate-600" />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {dashboardCards.map((card, index) => (
          <Card key={index} className="hover:shadow-lg transition-shadow duration-200 border-gray-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{card.title}</p>
                  <p className="text-2xl font-bold text-slate-800">{card.value}</p>
                  <p className="text-xs text-gray-500 mt-1">{card.subtitle}</p>
                </div>
                <div className={`p-3 rounded-xl ${card.color}`}>
                  <card.icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* Recent Activity */}
        <Card className="border-gray-200">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-800">Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentActivity.map((activity, index) => (
                <div key={index} className="flex items-start space-x-3 p-3 rounded-lg bg-slate-50">
                  <Badge variant="outline" className="mt-0.5 bg-white border-gray-300">
                    {activity.type}
                  </Badge>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-800">{activity.message}</p>
                    <p className="text-xs text-gray-500">{activity.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Upcoming Tasks */}
        <Card className="border-gray-200">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-800">Upcoming Tasks</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {upcomingTasks.map((task, index) => (
                <div key={index} className="flex items-center justify-between p-3 rounded-lg border border-gray-200 bg-white">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-800">{task.task}</p>
                    <p className="text-xs text-gray-500">Due: {task.dueDate}</p>
                  </div>
                  <Badge 
                    variant={task.priority === 'High' ? 'destructive' : task.priority === 'Medium' ? 'default' : 'secondary'}
                    className={task.priority === 'High' ? 'bg-red-100 text-red-800' : task.priority === 'Medium' ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-800'}
                  >
                    {task.priority}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;